import { useState } from 'react';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/icons';
import { IconButton } from '../IconButton/IconButton';
import { Text } from '../Text/Text';
import {
  InlineMessageAction,
  InlineMessageActionType,
} from './InlineMessageAction';
import { InlineMessageIcon } from './InlineMessageIcon';
import {
  inlineMessageContentStyles,
  inlineMessageStyles,
  inlineMessageTextGroupStyles,
} from './styles';
import { InlineMessageSizeType, InlineMessageVariantProps } from './types';

type InlineMessageProps = {
  instructions: string | React.ReactNode;
  title?: string;
  showLeadingIcon?: boolean;
  action?: InlineMessageActionType;
  canDismiss?: boolean;
  customIcon?: IconName;
  size?: InlineMessageSizeType;
} & InlineMessageVariantProps;

/**
 * Inline notifications show up in task flows to notify users of status changes,
 * or to provide additional information about specific content.
 * Inline notifications typically appear in close proximity to the primary content area.
 *
 * The action can either be a button a button link.
 *
 *
 * @example
 * <InlineMessage
 *  intent="default"
 *  title="Try google"
 *  instructions="To try google, click the link below"
 *  action={<Button>Learn more</Button>} />
 *
 * @example
 * <InlineMessage
 *  intent="info"
 *  title="Custom icon"
 *  instructions="This uses a custom icon instead of the default intent icon"
 *  customIcon="science" />
 */
export const InlineMessage = ({
  title,
  instructions,
  showLeadingIcon = true,
  action,
  canDismiss = false,
  intent,
  customIcon,
  size = 'default',
}: InlineMessageProps) => {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) {
    return null;
  }

  const isSmall = size === 'small';

  return (
    <div className={inlineMessageStyles({ intent, size })}>
      {showLeadingIcon && (
        <div className="flex justify-center items-center self-start py-m">
          <InlineMessageIcon intent={intent} customIcon={customIcon} />
        </div>
      )}
      <div
        className={inlineMessageContentStyles({
          size,
          canDismiss,
          hasAction: Boolean(action),
        })}
      >
        <div className={inlineMessageTextGroupStyles({ size })}>
          {title && (
            <Text as="h3" size={isSmall ? 'labelStrong' : 'headingXS'}>
              {title}
            </Text>
          )}
          {/* as="div" quells component inheritance warnings if instructions end up being li tags */}
          <Text as="div" size={isSmall ? 'bodyS' : 'bodyL'}>
            {instructions}
          </Text>
        </div>
        {action && <InlineMessageAction>{action}</InlineMessageAction>}
      </div>
      {canDismiss && (
        <div className="my-xxs">
          <IconButton
            aria-label="Dismiss message"
            intent="ghost"
            size="large"
            onClick={() => setIsDismissed(true)}
          >
            <Icon name="close" size="large" />
          </IconButton>
        </div>
      )}
    </div>
  );
};

InlineMessage.displayName = 'InlineMessage';
